/*
 * gmx-collection-filters.js — client-side facets + search for
 * sections/gmx-collection.liquid.
 *
 * Ports the behaviour of the mockup's collection.jsx / filters.jsx without
 * React: the products are already rendered by Liquid, so this shows and hides
 * the cards that are on the page. Standard sort choices reload through
 * Shopify. The Glitter's custom Spectral choice sorts its 48 rendered shades
 * by visible hue, then keeps neutral and multicolour shades at the end.
 *
 * Rules (identical to the mockup):
 *   - OR inside a facet group, AND across groups.
 *   - An empty group means "no constraint from this group".
 *   - The result count is live, in the toolbar and on the drawer's apply
 *     button.
 *
 * Deep links: `?family=blues` (comma-separate for several) preselects the
 * Color family facet on load, exactly as a click would — the homepage colour
 * tiles link that way. A slug with no option on the page is ignored, so the
 * shopper lands on the plain, unfiltered collection instead of an empty state.
 * The param is kept in sync with the selection afterwards, without a reload.
 *
 * The same option markup is printed twice (desktop dropdown + mobile drawer),
 * so every state change writes to ALL buttons that share a group/value pair.
 */
(function () {
  "use strict";

  /* Facet groups, in chip order. Each is OR-within, AND-across. */
  var GROUPS = ["family", "size", "theme"];

  /* Groups whose card value is a single string (`data-family`, `data-size`);
     "theme" is the odd one out — a card carries several themes. */
  var SINGLE = { family: "family", size: "size" };

  var FAMILY_PARAM = "family";

  /* Red -> orange -> yellow -> green -> blue -> purple -> pink, followed by
     neutrals and multicolour shades. Handles make the order stable when titles
     or colour-family labels change. Unknown future shades follow by name. */
  var SPECTRAL_HANDLES = [
    "red-apple-radiance-glitter",
    "watermelon-glitter",
    "claret-glitter",
    "coral-sea-glitter",
    "tulip-orange-glitter",
    "carrot-orange-glitter-1",
    "copper-penny-glitter",
    "rose-gold-glitter",
    "gold-bullion-glitter",
    "golden-rod",
    "champagne-glitter",
    "lemon-yellow-glitter",
    "chartreuse-glitter",
    "lime-green-glitter",
    "apple-green-glitter",
    "green-thumb-glitter",
    "kelly-green-dream-glitter",
    "midnight-blue-glitter-copy",
    "aquamarine-dream-glitter",
    "tiffany-blue-glitter",
    "aqua-glitter",
    "iridescent-cyan-glitter",
    "dazzling-blue-glitter",
    "light-blue-luster-glitter",
    "antique-blue-glitter",
    "royal-blue-streak-glitter",
    "jeweled-blue-glitter",
    "cobalt-blue-glitter",
    "midnight-blue-glitter",
    "iridescent-blue-rainbow-glitter",
    "iridescent-periwinkle-glitter",
    "iridescent-princess-purple-glitter",
    "princess-purple-glitter",
    "iridescent-rainbow-purple-glitter",
    "lavender-sky-glitter",
    "iridescent-lilac-glitter",
    "lavender-pink-iridescent-glitter",
    "cotton-candy-pink-glitter",
    "pink-rose-glitter",
    "rose-sparkler-glitter",
    "iridescent-magenta-glitter",
    "magenta-glitter",
    "hot-pink-love-glitter",
    "dazzling-white-glitter",
    "silver-steel-glitter",
    "platinum-glitter",
    "dragon-spell-glitter",
    "unicorn-glitter",
  ];
  var SPECTRAL_RANK = Object.create(null);
  SPECTRAL_HANDLES.forEach(function (handle, index) {
    SPECTRAL_RANK[handle] = index;
  });

  function init(root) {
    if (root.dataset.gmxCollectionReady === "true") return;
    root.dataset.gmxCollectionReady = "true";

    var grid = root.querySelector("[data-gmx-grid]");
    if (!grid) return;

    var chipsBox = root.querySelector("[data-gmx-chips]");
    var emptyBox = root.querySelector("[data-gmx-empty]");
    var drawer = root.querySelector("[data-gmx-drawer]");
    var sortControl = root.querySelector("[data-gmx-sort]");
    var sortToggle = sortControl && sortControl.querySelector("[data-gmx-sort-toggle]");
    var sortPanel = sortControl && sortControl.querySelector("[data-gmx-sort-panel]");
    var sortOptions = sortControl ? [].slice.call(sortControl.querySelectorAll("[data-gmx-sort-option]")) : [];
    var countTargets = root.querySelectorAll("[data-gmx-result-count]");
    var numberTargets = root.querySelectorAll("[data-gmx-result-number]");
    var activeCountEl = root.querySelector("[data-gmx-active-count]");
    var optionButtons = root.querySelectorAll("[data-gmx-option]");
    var searchInput = root.querySelector("[data-gmx-search]");
    var searchClear = root.querySelector("[data-gmx-search-clear]");
    var searchAll = root.querySelector("[data-gmx-search-all]");

    /* Cards, read once. themes is a Set of the product's (lowercased) tags. */
    /* Same normalisation as the CustomMarx search: lowercase, apostrophes
       stripped, so "Sadies" finds "Sadie's". */
    function norm(s) {
      return s.toLowerCase().replace(/[’'`]/g, "");
    }

    var items = [].slice.call(grid.querySelectorAll("[data-gmx-item]")).map(function (el) {
      return {
        el: el,
        /* The search haystack is title + tags, so a theme word ("halloween",
           "western") finds its designs even when no title carries it. */
        hay: norm((el.dataset.name || "") + " " + (el.dataset.themes || "")),
        name: el.dataset.name || "",
        handle: el.dataset.handle || "",
        size: el.dataset.size || "",
        family: el.dataset.family || "",
        themes: (el.dataset.themes || "").split(/\s+/).filter(Boolean),
      };
    });

    /* Selection state: group -> array of selected values. */
    var selected = {};
    GROUPS.forEach(function (group) {
      selected[group] = [];
    });
    var query = "";

    function applySpectralOrder() {
      items
        .slice()
        .sort(function (a, b) {
          var aKnown = Object.prototype.hasOwnProperty.call(SPECTRAL_RANK, a.handle);
          var bKnown = Object.prototype.hasOwnProperty.call(SPECTRAL_RANK, b.handle);
          if (aKnown && bKnown) return SPECTRAL_RANK[a.handle] - SPECTRAL_RANK[b.handle];
          if (aKnown) return -1;
          if (bKnown) return 1;
          return a.name.localeCompare(b.name);
        })
        .forEach(function (item) {
          grid.appendChild(item.el);
        });
    }

    var selectedSort = sortPanel && sortPanel.querySelector('[data-gmx-sort-option][aria-selected="true"]');
    if (selectedSort && selectedSort.dataset.gmxSortOption === "spectral") applySpectralOrder();

    /* Human labels for the chips, keyed "group::value". */
    var labels = {};
    [].forEach.call(optionButtons, function (btn) {
      labels[btn.dataset.group + "::" + btn.dataset.value] = btn.dataset.label || btn.dataset.value;
    });

    function activeCount() {
      return GROUPS.reduce(function (n, group) {
        return n + selected[group].length;
      }, 0);
    }

    function matches(item) {
      if (query && item.hay.indexOf(query) === -1) return false;
      for (var i = 0; i < GROUPS.length; i++) {
        var group = GROUPS[i];
        var chosen = selected[group];
        if (!chosen.length) continue;
        if (SINGLE[group]) {
          if (chosen.indexOf(item[SINGLE[group]]) === -1) return false;
        } else {
          var hit = item.themes.some(function (t) {
            return chosen.indexOf(t) !== -1;
          });
          if (!hit) return false;
        }
      }
      return true;
    }

    /* The family selection and the search query are the state carried in the
       URL (rewritten in place, so Back still leaves the collection page).
       That makes a filtered view shareable: `?q=halloween` on any collection
       is a hard link to its pre-searched grid. */
    function syncUrl() {
      if (!window.history || !window.history.replaceState || typeof URL !== "function") return;
      try {
        var url = new URL(window.location.href);
        if (selected.family.length) url.searchParams.set(FAMILY_PARAM, selected.family.join(","));
        else url.searchParams.delete(FAMILY_PARAM);
        var raw = searchInput ? searchInput.value.trim() : "";
        if (raw !== "") url.searchParams.set("q", raw);
        else url.searchParams.delete("q");
        window.history.replaceState(null, "", url.toString());
      } catch (err) {
        /* No URL support: the filters still work, the address bar just lags. */
      }
    }

    /* `?family=blues,greens` -> preselected, ignoring slugs this page has no
       option for (a family with no products lands on the full collection). */
    function readQuery() {
      /* `?q=halloween` prefills the search field, exactly as typing would. */
      var qMatch = /[?&]q=([^&#]*)/.exec(window.location.search);
      if (qMatch && searchInput) {
        var qRaw = decodeURIComponent(qMatch[1].replace(/\+/g, " ")).trim();
        if (qRaw !== "") {
          searchInput.value = qRaw;
          query = norm(qRaw);
        }
      }

      var match = /[?&]family=([^&#]*)/.exec(window.location.search);
      if (!match) return;
      /* Prototype-free: `?family=constructor` must not read as a known slug. */
      var known = Object.create(null);
      [].forEach.call(optionButtons, function (btn) {
        if (btn.dataset.group === "family") known[btn.dataset.value] = true;
      });
      decodeURIComponent(match[1].replace(/\+/g, " "))
        .split(",")
        .forEach(function (raw) {
          var slug = raw.trim().toLowerCase();
          if (known[slug] && selected.family.indexOf(slug) === -1) selected.family.push(slug);
        });
    }

    function toggle(group, value) {
      var list = selected[group];
      if (!list) return;
      var at = list.indexOf(value);
      if (at === -1) list.push(value);
      else list.splice(at, 1);
      render();
    }

    function clearAll() {
      GROUPS.forEach(function (group) {
        selected[group] = [];
      });
      query = "";
      if (searchInput) searchInput.value = "";
      render();
    }

    function renderChips() {
      if (!chipsBox) return;
      chipsBox.textContent = "";
      var total = activeCount();
      chipsBox.hidden = total === 0;
      if (total === 0) return;

      GROUPS.forEach(function (group) {
        selected[group].forEach(function (value) {
          var chip = document.createElement("button");
          chip.type = "button";
          chip.className = "gmx-chip";
          chip.innerHTML = '<span class="gmx-chip__label"></span><span class="gmx-chip__x" aria-hidden="true">✕</span>';
          chip.querySelector(".gmx-chip__label").textContent = labels[group + "::" + value] || value;
          chip.setAttribute("aria-label", "Remove filter " + (labels[group + "::" + value] || value));
          chip.addEventListener("click", function () {
            toggle(group, value);
          });
          chipsBox.appendChild(chip);
        });
      });

      var clear = document.createElement("button");
      clear.type = "button";
      clear.className = "gmx-chip-clear";
      clear.textContent = "Clear all";
      clear.addEventListener("click", clearAll);
      chipsBox.appendChild(clear);
    }

    function render() {
      /* 1. Option buttons — every copy of the same option. */
      [].forEach.call(optionButtons, function (btn) {
        var on = selected[btn.dataset.group] && selected[btn.dataset.group].indexOf(btn.dataset.value) !== -1;
        btn.setAttribute("aria-pressed", on ? "true" : "false");
        btn.classList.toggle("is-on", !!on);
      });

      /* 2. Per-group pill on the desktop triggers. */
      [].forEach.call(root.querySelectorAll("[data-gmx-dropdown-toggle]"), function (trigger) {
        var group = trigger.dataset.group;
        var n = (selected[group] || []).length;
        var pill = trigger.querySelector("[data-gmx-group-count]");
        if (pill) {
          pill.textContent = String(n);
          pill.hidden = n === 0;
        }
        trigger.classList.toggle("is-active", n > 0);
      });

      /* 3. Mobile "Filters" badge. */
      if (activeCountEl) {
        activeCountEl.textContent = String(activeCount());
        activeCountEl.hidden = activeCount() === 0;
      }

      /* 4. Filter the cards. Shopify supplies the selected collection order. */
      var visible = [];
      items.forEach(function (item) {
        var on = matches(item);
        item.el.hidden = !on;
        if (on) visible.push(item);
      });

      /* 5. Counts, chips, empty state. */
      var word = visible.length === 1 ? " design" : " designs";
      [].forEach.call(countTargets, function (el) {
        el.textContent = visible.length + word;
      });
      [].forEach.call(numberTargets, function (el) {
        el.textContent = String(visible.length);
      });
      /* An empty collection keeps Liquid's own "no products" message; the
         filter empty state only speaks for filters that matched nothing. */
      var filteredToNothing = visible.length === 0 && items.length > 0;
      if (emptyBox) emptyBox.hidden = !filteredToNothing;
      grid.hidden = filteredToNothing;

      /* 6. Search chrome: clear button, and the empty state's store-wide
         search escape hatch (only offered when a query is active). */
      var raw = searchInput ? searchInput.value.trim() : "";
      if (searchClear) searchClear.hidden = raw === "";
      if (searchAll) {
        searchAll.hidden = raw === "";
        if (raw !== "") {
          var base = searchAll.getAttribute("href").split("?")[0];
          searchAll.setAttribute("href", base + "?q=" + encodeURIComponent(raw));
        }
      }

      renderChips();
      syncUrl();
    }

    /* ---- Option clicks (dropdowns and drawer share the markup) ---- */
    [].forEach.call(optionButtons, function (btn) {
      btn.addEventListener("click", function () {
        toggle(btn.dataset.group, btn.dataset.value);
      });
    });

    /* ---- Clear-all controls (drawer footer + empty state) ---- */
    [].forEach.call(root.querySelectorAll("[data-gmx-clear]"), function (btn) {
      btn.addEventListener("click", clearAll);
    });

    /* ---- Desktop dropdowns ---- */
    var dropdowns = [].slice.call(root.querySelectorAll("[data-gmx-dropdown]"));

    function closeDropdowns(except) {
      dropdowns.forEach(function (dd) {
        if (dd === except) return;
        var panel = dd.querySelector("[data-gmx-dropdown-panel]");
        var trigger = dd.querySelector("[data-gmx-dropdown-toggle]");
        if (panel) panel.hidden = true;
        if (trigger) trigger.setAttribute("aria-expanded", "false");
      });
    }

    dropdowns.forEach(function (dd) {
      var trigger = dd.querySelector("[data-gmx-dropdown-toggle]");
      var panel = dd.querySelector("[data-gmx-dropdown-panel]");
      if (!trigger || !panel) return;
      trigger.addEventListener("click", function () {
        var willOpen = panel.hidden;
        setSortOpen(false, false);
        closeDropdowns(dd);
        panel.hidden = !willOpen;
        trigger.setAttribute("aria-expanded", willOpen ? "true" : "false");
      });
    });

    document.addEventListener("mousedown", function (event) {
      if (!root.contains(event.target) || !event.target.closest("[data-gmx-dropdown]")) closeDropdowns(null);
      if (!sortControl || !sortControl.contains(event.target)) setSortOpen(false, false);
    });

    document.addEventListener("focusin", function (event) {
      if (sortControl && !sortControl.contains(event.target)) setSortOpen(false, false);
    });

    /* ---- Mobile drawer ---- */
    function setDrawer(open) {
      if (!drawer) return;
      drawer.hidden = !open;
      document.documentElement.classList.toggle("gmx-no-scroll", open);
    }

    var drawerOpen = root.querySelector("[data-gmx-drawer-open]");
    if (drawerOpen) {
      drawerOpen.addEventListener("click", function () {
        setDrawer(true);
      });
    }
    [].forEach.call(root.querySelectorAll("[data-gmx-drawer-close]"), function (btn) {
      btn.addEventListener("click", function () {
        setDrawer(false);
      });
    });

    document.addEventListener("keydown", function (event) {
      if (event.key !== "Escape") return;
      closeDropdowns(null);
      setSortOpen(false, false);
      setDrawer(false);
    });

    /* ---- Search ---- */
    if (searchInput) {
      searchInput.addEventListener("input", function () {
        query = norm(searchInput.value.trim());
        render();
      });
    }
    if (searchClear) {
      searchClear.addEventListener("click", function () {
        if (searchInput) {
          searchInput.value = "";
          searchInput.focus();
        }
        query = "";
        render();
      });
    }

    /* ---- Sort menu ---- */
    function setSortOpen(open, focusSelected) {
      if (!sortToggle || !sortPanel) return;
      sortPanel.hidden = !open;
      sortToggle.setAttribute("aria-expanded", open ? "true" : "false");
      if (open && focusSelected) {
        var selectedOption = sortPanel.querySelector('[aria-selected="true"]');
        if (selectedOption) selectedOption.focus();
      }
    }

    function selectSort(option) {
      if (!option) return;
      var sortValue = option.dataset.gmxSortOption || "best-selling";
      sortOptions.forEach(function (candidate) {
        var selectedOption = candidate === option;
        candidate.classList.toggle("is-selected", selectedOption);
        candidate.setAttribute("aria-selected", selectedOption ? "true" : "false");
      });
      if (sortToggle) {
        var label = option.querySelector("span");
        var labelText = label ? label.textContent.trim() : option.textContent.trim();
        var triggerLabel = sortToggle.querySelector("[data-gmx-sort-label]");
        if (triggerLabel) triggerLabel.textContent = labelText;
        sortToggle.setAttribute("aria-label", "Sort by: " + labelText);
      }
      setSortOpen(false, false);
      try {
        var url = new URL(window.location.href);
        if (sortValue === "spectral") url.searchParams.delete("sort_by");
        else url.searchParams.set("sort_by", sortValue);
        url.searchParams.delete("page");
        window.location.assign(url.toString());
      } catch (err) {
        window.location.search = sortValue === "spectral" ? "" : "?sort_by=" + encodeURIComponent(sortValue);
      }
    }

    function focusSortOption(from, offset) {
      var index = sortOptions.indexOf(from);
      if (index === -1 || !sortOptions.length) return;
      sortOptions[(index + offset + sortOptions.length) % sortOptions.length].focus();
    }

    if (sortToggle && sortPanel) {
      sortToggle.addEventListener("click", function () {
        var willOpen = sortPanel.hidden;
        closeDropdowns(null);
        setSortOpen(willOpen, false);
      });
      sortToggle.addEventListener("keydown", function (event) {
        if (event.key !== "ArrowDown" && event.key !== "ArrowUp") return;
        event.preventDefault();
        closeDropdowns(null);
        setSortOpen(true, true);
      });
      sortOptions.forEach(function (option) {
        option.addEventListener("click", function () {
          selectSort(option);
        });
        option.addEventListener("keydown", function (event) {
          if (event.key === "ArrowDown" || event.key === "ArrowUp") {
            event.preventDefault();
            focusSortOption(option, event.key === "ArrowDown" ? 1 : -1);
          } else if (event.key === "Home" || event.key === "End") {
            event.preventDefault();
            sortOptions[event.key === "Home" ? 0 : sortOptions.length - 1].focus();
          } else if (event.key === "Escape") {
            event.preventDefault();
            setSortOpen(false, false);
            sortToggle.focus();
          }
        });
      });
    }

    readQuery();
    render();
  }

  /* ---- Banner "Read more" ----------------------------------------------
   * The animation itself is pure CSS: `__reveal` is a one-row grid that goes
   * from `minmax(3 lines, 0fr)` to `minmax(3 lines, 1fr)` (see
   * gmx-collection.css). No height is measured here and nothing is written to
   * a custom property, so there is no value that can go stale between a resize
   * and a click — that staleness was one of the failure modes of the old
   * max-height version.
   *
   * This function does two things only:
   *   1. Toggles `data-gmx-fold` and `aria-expanded` on click.
   *   2. Drops the fold entirely when the copy already fits in three lines at
   *      the current width. The Liquid picks the fold by character count,
   *      which is a guess; this is the real measurement, and it re-runs on
   *      resize and once fonts have loaded.
   *
   * Lives outside init() because the Show Pony line-up has no product grid,
   * and init() returns early without one.
   */
  function initDisclosure(desc) {
    if (desc.dataset.gmxDescReady === "true") return;
    desc.dataset.gmxDescReady = "true";

    var reveal = desc.querySelector(".gmx-coll-banner__reveal");
    var text = desc.querySelector(".gmx-coll-banner__text");
    var toggle = desc.querySelector("[data-gmx-desc-toggle]");
    if (!reveal || !text || !toggle) return;

    function setState(open) {
      desc.setAttribute("data-gmx-fold", open ? "open" : "closed");
      toggle.setAttribute("aria-expanded", open ? "true" : "false");
    }

    /* True when the text overflows the three-line floor. scrollHeight is the
       full content height even while the grid row clips it, so this reads
       correctly in either state. */
    function needsFold() {
      var cs = getComputedStyle(text);
      var line = parseFloat(cs.lineHeight);
      /* `line-height: normal` computes to the keyword in some engines. */
      if (!line) line = parseFloat(cs.fontSize) * 1.5;
      return text.scrollHeight > line * 3 + 1;
    }

    function refresh() {
      if (needsFold()) {
        if (!desc.hasAttribute("data-gmx-fold")) setState(false);
      } else {
        desc.removeAttribute("data-gmx-fold");
        toggle.setAttribute("aria-expanded", "true");
      }
    }

    toggle.addEventListener("click", function () {
      setState(desc.getAttribute("data-gmx-fold") !== "open");
    });

    refresh();
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(refresh);

    var timer = null;
    var width = window.innerWidth;
    window.addEventListener("resize", function () {
      /* The line count only changes when the line box changes, i.e. on width. */
      if (window.innerWidth === width) return;
      width = window.innerWidth;
      clearTimeout(timer);
      timer = setTimeout(refresh, 120);
    });
  }

  function boot() {
    [].forEach.call(document.querySelectorAll("[data-gmx-collection]"), init);
    [].forEach.call(document.querySelectorAll("[data-gmx-desc]"), initDisclosure);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }

  /* Theme editor re-renders the section without a page load. */
  document.addEventListener("shopify:section:load", boot);
})();
